const isObject = (item: unknown): item is Record<string, unknown> => {
    return item !== null && typeof item === 'object';
};

/**
 * Merges two objects deeply, combining their properties recursively.
 * If a property in the source object is an object and not an array,
 * it will be merged with the corresponding property in the target object.
 * Otherwise, the property from the source object will overwrite the one in the target object.
 *
 * @template T - The type of the target object.
 * @template U - The type of the source object.
 * @param {T} target - The target object to merge into.
 * @param {U} source - The source object to merge from.
 * @returns {T & U} A new object that is the result of deeply merging the target and source objects.
 */
const deepMerge = <T extends object, U extends object>(target: T, source: U): T & U => {
    const output: Partial<T & U> = { ...target };

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key) && isObject(source[key]) && !Array.isArray(source[key])) {
            output[key] = deepMerge((target as Record<string, unknown>)[key] ?? {}, source[key]) as (T & U)[typeof key];
        } else {
            output[key] = source[key] as (T & U)[typeof key];
        }
    }

    return output as T & U;
};

export default deepMerge;
